import { Timestamp } from "firebase/firestore";

// Entity types
export const ENTITY_TYPES = [
  "DESTINATION",
  "PARK",
  "EVENT",
  "HOTEL",
  "SHIP",
  "AIRPORT",
  "ATTRACTION",
  "EXPERIENCE",
  "RESTAURANT",
  "TRANSPORTATION",
  "LOCATION",
] as const;

export type EntityType = typeof ENTITY_TYPES[number];

/**
 * Types that were merged into others in October 2026, and what they became.
 * Places and typed-in activities saved before the merge (and older watch
 * builds) can still carry these, so every read of a type goes through
 * `normalizeEntityType`.
 *
 *   Lounge → Food & drink: the two logged the same steps, and coffee shops
 *     and bars had landed on both sides.
 *   Show, Excursion → Experience: anything with a start time you watch or take
 *     part in.
 *   Cruise line → Destination: a brand that groups ships, like a resort
 *     groups parks.
 *   Shop, Port → Location: somewhere you arrived and left.
 */
export const LEGACY_ENTITY_TYPES: Readonly<Record<string, EntityType>> = {
  LOUNGE: "RESTAURANT",
  SHOW: "EXPERIENCE",
  EXCURSION: "EXPERIENCE",
  CRUISE_LINE: "DESTINATION",
  SHOP: "LOCATION",
  PORT: "LOCATION",
};

/** A stored type as one of today's types, or null when it is none of them. */
export function normalizeEntityType(raw: unknown): EntityType | null {
  if (typeof raw !== "string") return null;
  if ((ENTITY_TYPES as readonly string[]).includes(raw)) return raw as EntityType;
  return LEGACY_ENTITY_TYPES[raw] ?? null;
}

/**
 * Every stored value that means one of these types: the types themselves plus
 * the old names merged into them. Use it in a Firestore `where("type", "in", …)`
 * so a place saved under an old name is still found.
 */
export function storedEntityTypes(types: readonly EntityType[]): string[] {
  const out = new Set<string>(types);
  for (const [old, current] of Object.entries(LEGACY_ENTITY_TYPES)) {
    if (types.includes(current)) out.add(old);
  }
  return [...out];
}

/**
 * An entity read from Firestore, with its type brought up to date. Use it
 * wherever a place document is turned into an `Entity`.
 */
export function withCurrentType<T extends object>(data: T): T {
  const raw = (data as { type?: unknown }).type;
  const type = normalizeEntityType(raw);
  return type && type !== raw ? { ...data, type } : data;
}

// Event types
export const EVENT_TYPES = [
  "WAIT_START",
  "WAIT_END",
  "MAIN_EVENT_START",
  "MAIN_EVENT_END",
  "DELAY_START",
  "DELAY_END",
  "PRESHOW_START",
  "PRESHOW_END",
  "SEATED",
  "ORDER_PLACED",
  "ORDER_RECEIVED",
  "DINING_DONE",
  // A take-away order handed over: the order's arrival and the end of the
  // visit in one tap.
  "PICKED_UP",
  "RIDE_REQUESTED",
  "RIDE_ACCEPTED",
  "VEHICLE_ARRIVED",
  "IN_TRANSIT",
  "ARRIVED",
  "CHECK_IN",
  "SECURITY",
  "BOARDING",
  "DEPARTED",
  "LANDED",
  "PROGRESS_UPDATE",
] as const;

export type EventType = typeof EVENT_TYPES[number];

// Event type categories for grouping and metric calculations
export const EVENT_TYPE_CATEGORIES = [
  "START",
  "ACTIVE",
  "END",
  "MILESTONE",
  "DELAY_START",
  "DELAY_END",
] as const;

export type EventTypeCategory = typeof EVENT_TYPE_CATEGORIES[number];

// Maps each EventType to its category
export const EVENT_TYPE_CATEGORY_MAP: Record<EventType, EventTypeCategory> = {
  WAIT_START: "START",
  WAIT_END: "ACTIVE",       // wait ending = experience becoming active
  MAIN_EVENT_START: "ACTIVE",
  MAIN_EVENT_END: "END",
  DELAY_START: "DELAY_START",
  DELAY_END: "DELAY_END",
  PRESHOW_START: "MILESTONE",
  PRESHOW_END: "MILESTONE",
  SEATED: "MILESTONE",
  ORDER_PLACED: "MILESTONE",
  ORDER_RECEIVED: "MILESTONE",
  DINING_DONE: "END",
  PICKED_UP: "END",
  RIDE_REQUESTED: "START",
  RIDE_ACCEPTED: "MILESTONE",
  VEHICLE_ARRIVED: "MILESTONE",
  IN_TRANSIT: "ACTIVE",
  ARRIVED: "END",
  CHECK_IN: "MILESTONE",
  SECURITY: "MILESTONE",
  BOARDING: "MILESTONE",
  DEPARTED: "ACTIVE",
  LANDED: "END",
  PROGRESS_UPDATE: "MILESTONE",
};

// Connection
export interface Connection {
  displayName: string;
  avatarUrl: string | null;
  email: string | null;
  linkedUserId: string | null;
  status: "pending" | "accepted";
}

// User
export interface UserProfile {
  email: string;
  displayName: string;
  avatarUrl: string | null;
  enabled: boolean;
  defaultLocationSharing: boolean;
  roles: string[];
  connections: Record<string, Connection>;
  lastActive: Timestamp | null;
  roleHistory?: RoleHistoryEntry[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Audit log entry recorded each time a user's roles change. Stored as
 * `roleHistory` array on the user doc; appended via `arrayUnion`.
 */
export interface RoleHistoryEntry {
  changedBy: string;
  changedByName: string;
  changedAt: Timestamp;
  previousRoles: string[];
  newRoles: string[];
  action: string;
}

// Travel party member
export interface TravelPartyMember {
  displayName: string;
  avatarUrl: string | null;
  userId: string | null;
  connectionId: string | null;
  role: "organizer" | "member";
  locationSharingEnabled: boolean | null;
  status: "confirmed" | "pending";
}

// Person (staff/contact)
export interface Person {
  role: string;
  name: string;
}

// Cruise-specific config carried on a cruise-type segment
export interface CruiseConfig {
  cruiseLineEntityId: string | null;
  shipEntityId: string | null;
  homePort: string | null;
  nights: number | null;
}

// Trip
export interface Trip {
  name: string;
  startDate: Timestamp;
  endDate: Timestamp;
  /**
   * IANA zone the trip ran on, e.g. "America/New_York". Times render against
   * it rather than the reader's device, so a trip reads the same from Boston
   * as it did from the park. Absent on older trips, which fall back to the
   * reader's own zone — the behavior before this field existed.
   */
  timezone?: string | null;
  notes: string | null;
  createdBy: string;
  memberUserIds: string[];
  travelParty: Record<string, TravelPartyMember>;
  people: Person[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Activity option
export interface ActivityOption {
  id: string;
  displayName: string;
  optionType: string;
}

// Event option
//
// The flow fields (`start`, `next`, `requires`, `once`) describe the activity's
// state machine: which steps open it, what each step can lead to, and what has
// to have happened first. They live on the option rather than in a table beside
// it so that an entity's `eventOptions` array carries its whole flow — copy the
// array and you have copied the workflow. See `utils/activityFlow`.
export interface EventOption {
  id: string;
  displayName: string;
  eventType: EventType;
  category: EventTypeCategory;
  isCustom?: boolean;
  /** This step can be the first one logged (a walk-on ride, a walk-up table). */
  start?: boolean;
  /**
   * Ids of the steps that may follow this one, most likely first. An empty
   * array means the activity is over. Left undefined, the step isn't wired into
   * the flow and what follows it is guessed (see `legacyNextQuickOptions`).
   */
  next?: string[];
  /**
   * Ids of steps that must already be logged before this one is offered:
   * "Preshow End" needs "Preshow Start"; VelociCoaster's "On Ride" needs "At
   * Lockers", because the ride won't take you with anything in your pockets.
   */
  requires?: string[];
  /**
   * This step happens at most once, so it stops being offered once it's
   * logged: you sit down once, however many rounds you order.
   */
  once?: boolean;
}

// Time input mode for entities — determines what the user is prompted for
// when logging an activity against this entity.
// "flight_times" asks for a departure and a boarding time: the departure in
// `customResponses.scheduled_time` (where a flight's one time always went),
// boarding in `customResponses.scheduled_boarding`.
export const TIME_INPUT_MODES = ["posted_wait", "scheduled_time", "flight_times", "none"] as const;
export type TimeInputMode = typeof TIME_INPUT_MODES[number];

// Custom questions — per-entity fields asked when logging there (the song on
// Guardians, the booth at a festival stand). Defined on
// `entity.customConfig.custom_questions`, answered into
// `interaction.customResponses[question.id]`. The shape and the storage keys
// are the old app's, so answers carried over by the migration are read as-is.
export const CUSTOM_QUESTION_TYPES = [
  "text",
  "textarea",
  "number",
  "select",
  "date",
  "time",
  "datetime",
] as const;
export type CustomQuestionType = typeof CUSTOM_QUESTION_TYPES[number];

export interface CustomQuestion {
  /** The key the answer is stored under in `customResponses`; stable once set. */
  id: string;
  label: string;
  type: CustomQuestionType;
  required: boolean;
  /** Position among the entity's questions, 1-based. */
  order: number;
  /** Choices for a `select` question; absent for every other type. */
  options?: string[];
}

// Entity
export interface Entity {
  displayName: string;
  type: EntityType;
  imageUrl: string | null;
  parentId: string | null;
  externalId: string | null;
  externalSource: string | null;
  activeFrom: Timestamp | null;
  activeTo: Timestamp | null;
  /**
   * The entity's own window intersected with every ancestor's, maintained by
   * the `onentitywrite` Cloud Function. Retiring a park's Halloween event
   * retires the houses saved under it without dating each one, and clients get
   * to answer "was this a place on this date?" with a two-field comparison
   * instead of walking `parentId` themselves (`utils/entityActive.ts`).
   *
   * Absent on entities not yet touched by the trigger or the backfill job, and
   * in the moment between a save and the trigger firing; `entityActive` falls
   * back to the entity's own dates there.
   */
  effectiveActiveFrom?: Timestamp | null;
  effectiveActiveTo?: Timestamp | null;
  customConfig: Record<string, unknown>;
  activityOptions: ActivityOption[];
  eventOptions: EventOption[];
  location: { latitude: number; longitude: number } | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Shared, in-app settings for an activity type (`typeSettings/{TYPE}`), layered
 * over the built-in defaults in `src/config`. Every field is optional: absent
 * means "use the built-in default".
 */
export interface TypeSettings {
  /** The staff roles offered up front on this type's activities. */
  staffRoles?: { value: string; label: string }[];
  /** The log steps (and their flow) for a place of this type without its own. */
  eventOptions?: EventOption[];
  /** Whether logging asks for a posted wait or a scheduled time. */
  timeInputMode?: TimeInputMode;
  /** Questions asked when logging, for a place of this type without its own. */
  customQuestions?: CustomQuestion[];
  updatedAt?: Timestamp;
}

// Interaction event
export interface InteractionEvent {
  eventType: EventType;
  eventTimestamp: Timestamp;
  notes: string | null;
  customLabel: string | null;
  // Set (0-based) when this event is a read-only projection of a dining round's
  // placed/received time. Such events are regenerated from the round on save and
  // are not directly editable in the timeline; the round is the source of truth.
  roundIndex?: number | null;
  /**
   * IANA zone of the device that logged this event ("America/New_York"), so
   * the time reads the way it did on the clock where it happened. A flight
   * boards on Boston time and lands on Pacific; each event keeps its own.
   * Absent on events logged before this existed, which fall back to the
   * activity's zone, then its segment's, the trip's, and the reader's own.
   */
  timezone?: string | null;
}

// Photo
export interface Photo {
  url: string;
  thumbnailUrl: string | null;
  caption: string | null;
}

// Interaction
export interface Interaction {
  userId: string;
  tripId: string;
  segmentId: string | null;
  /**
   * The activity this one happened inside of, when it happened inside one:
   * the rides logged under a park check-in, the lounge and the gate run
   * logged under an airport, dinner logged under the hotel you checked into.
   *
   * A segment is the stretch of the trip ("park week", "the cruise"); this is
   * the place you were standing while you logged it, which is often a thing
   * you also logged. Null means the activity hangs directly off its segment,
   * which is how everything logged before this field existed behaves.
   *
   * A child always carries its parent's `segmentId` too, so every existing
   * "activities of this segment" query still sees the whole tree — the nesting
   * only changes how the list is drawn. `utils/activityTree.ts` builds and
   * maintains the tree; moving a parent between segments takes its descendants
   * with it.
   */
  parentInteractionId?: string | null;
  entityId: string | null;
  name: string | null;
  notes: string | null;
  postedWait: number | null;
  accessTierId: string | null;
  participants: string[];
  people: Person[];
  events: InteractionEvent[];
  photos: Photo[];
  customResponses: Record<string, unknown>;
  /**
   * Where this activity sat in the note it was transcribed from, so a
   * backfilled one without event times still lands in the order it was
   * written. Absent on anything logged in the app, which carries real times.
   */
  sortIndex?: number | null;
  /**
   * Set when this activity is deliberately not tied to a place — breakfast,
   * the safety drill, a pool afternoon, a flight home. Without it these are
   * indistinguishable from the ones still waiting to be linked, so the
   * "unlinked" backlog never empties and the real work stays buried.
   *
   * It says "asked and answered", not "unknown": an activity with neither
   * `entityId` nor this flag is still an open question.
   */
  noPlace?: boolean | null;
  /**
   * IANA zone of the device that created the activity. Stands in for an
   * event's zone when the event carries none (and for an activity with no
   * events yet), so a day's grouping follows where the activity happened.
   */
  timezone?: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Invite
export interface Invite {
  connectionId: string;
  fromUserId: string;
  fromDisplayName: string;
  toEmail: string;
  tripId: string | null;
  status: "pending" | "accepted" | "declined";
  sentAt: Timestamp;
  respondedAt: Timestamp | null;
}

// ========== Segments ==========

// Segment "kind" — chosen per segment so a single trip can mix a theme-park
// stretch, a cruise, and free-form groupings. Drives which affordances show.
// A "travel" segment is a door-to-door travel day: its legs (ride share,
// airport, flight, airport, ride share) are activities side by side in order,
// and the segment shows the day's route and door-to-door time (utils/travelDay).
export const SEGMENT_TYPES = ["general", "cruise", "themepark", "travel"] as const;
export type SegmentType = typeof SEGMENT_TYPES[number];

// Cruise itinerary day types
export const CRUISE_DAY_TYPES = ["embark", "sea", "port", "disembark"] as const;
export type CruiseDayType = typeof CRUISE_DAY_TYPES[number];

// Segment - subcollection of trips (nestable via parentSegmentId)
//
// A cruise segment (`segmentType: 'cruise'`) carries `cruiseConfig` and its
// itinerary days hang off it as child segments (`parentSegmentId` = the cruise
// segment) with `dayNumber`/`dayType` populated and `startDate === endDate`.
// General/theme-park segments are free-form and nestable as before.
export interface Segment {
  name: string;
  /**
   * IANA zone this segment's days and times ran on, when it differs from the
   * trip's. A cruise that moved its clocks mid-sailing sets it per day.
   * Absent means "whatever the parent segment or the trip says".
   */
  timezone?: string | null;
  parentSegmentId: string | null; // null = top-level segment
  entityId: string | null; // primary entity (ship, resort, etc.)
  startDate: Timestamp;
  endDate: Timestamp;
  /**
   * Who was on this stretch of the trip, as travel-party member keys — the
   * grandparents who only came for the cruise, the friend who joined for a
   * park day. Absent or empty means "whatever the parent segment says", and
   * ultimately the whole travel party, so an untouched segment behaves as it
   * always did.
   *
   * Activities under the segment inherit it as their default "who was here":
   * an activity carries no list of its own until someone deselects a member on
   * it, so changing the segment's group updates every activity that never
   * disagreed with it.
   */
  participants?: string[] | null;
  people: Person[];
  notes: string | null;
  customConfig: Record<string, unknown>;
  // Optional; treat a missing value as "general".
  segmentType?: SegmentType;
  cruiseConfig?: CruiseConfig | null;
  // Cruise itinerary-day fields (null for non-day segments)
  dayNumber?: number | null; // 1-based
  dayType?: CruiseDayType | null;
  portName?: string | null;
  portEntityId?: string | null; // optional link to a PORT-type entity
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/** Read a segment's type, defaulting to "general" for older docs. */
export function getSegmentType(segment: { segmentType?: SegmentType } | null | undefined): SegmentType {
  return segment?.segmentType ?? "general";
}

/** True if a segment is a cruise (has its own itinerary days as children). */
export function isCruiseSegment(segment: { segmentType?: SegmentType } | null | undefined): boolean {
  return getSegmentType(segment) === "cruise";
}

/** True if a segment is a travel day (see utils/travelDay). */
export function isTravelSegment(segment: { segmentType?: SegmentType } | null | undefined): boolean {
  return getSegmentType(segment) === "travel";
}

/** True if a segment represents a cruise itinerary day. */
export function isCruiseDay(segment: { dayType?: CruiseDayType | null; dayNumber?: number | null } | null | undefined): boolean {
  return !!segment && (segment.dayType != null || segment.dayNumber != null);
}

// ========== Custom Responses (Type-specific interaction data) ==========

// Dining: per-participant orders
export interface DiningOrder {
  participantId: string; // travel party member UUID or "SHARED"
  order: string;
}

// Lounge: multi-round ordering. Each round can optionally carry its own
// placed/received timestamps so per-round service time is tracked independently
// of the top-level ORDER_PLACED/ORDER_RECEIVED timeline events.
export interface LoungeRound {
  orders: DiningOrder[];
  placedAt?: Timestamp | null;
  receivedAt?: Timestamp | null;
}

// Trivia: question tracking
export interface TriviaQuestion {
  question: string;
  answer: string;
  points: number;
  correctAnswer: string;
}

// Discriminated union for customResponses when typed
export interface DiningCustomResponses {
  activityType: "dining";
  orders: DiningOrder[];
}

export interface LoungeCustomResponses {
  activityType: "lounge";
  rounds: LoungeRound[];
}

export interface TriviaCustomResponses {
  activityType: "trivia";
  score: string;
  winningScore: string;
  questions: TriviaQuestion[];
}

export type TypedCustomResponses =
  | DiningCustomResponses
  | LoungeCustomResponses
  | TriviaCustomResponses;

/**
 * Entity types offered when typing an entity-less ("custom") interaction,
 * either at creation time or later via edit. Picking one gives the interaction
 * the right event defaults (e.g. a restaurant's Seated / Order Placed steps)
 * and order tracking. The broad container types (destinations, parks, ships…)
 * are omitted since they don't describe a single logged activity — but a hotel
 * does (checking in, arriving for the night), so it's offered here even though
 * hotels can also hold child entities.
 */
export const CUSTOM_INTERACTION_TYPES: EntityType[] = [
  "RESTAURANT",
  "ATTRACTION",
  "EXPERIENCE",
  "EVENT",
  "TRANSPORTATION",
  "AIRPORT",
  "HOTEL",
  "LOCATION",
];

/**
 * Resolve the effective entity type for an interaction. A linked entity uses
 * its own type; an entity-less ("custom") interaction can carry a chosen type
 * in `customResponses.entityType` so it still gets type-specific event defaults
 * (e.g. a restaurant's Seated / Order Placed / Done) and order tracking.
 */
export function getInteractionEntityType(
  entity: { type?: EntityType } | null | undefined,
  customResponses: Record<string, unknown> | null | undefined,
): EntityType | undefined {
  const own = normalizeEntityType(entity?.type);
  if (own) return own;
  return normalizeEntityType(customResponses?.entityType) ?? undefined;
}

/**
 * True when an interaction should surface order/round tracking — food & drink
 * places by type, dining/lounge quick-activities, or anything that already
 * has orders recorded.
 */
export function interactionUsesOrders(
  type: EntityType | string | undefined,
  customResponses: Record<string, unknown> | null | undefined,
): boolean {
  if (normalizeEntityType(type) === "RESTAURANT") return true;
  const at = customResponses?.activityType;
  if (at === "dining" || at === "lounge") return true;
  const orders = customResponses?.orders;
  if (Array.isArray(orders) && orders.length > 0) return true;
  const rounds = customResponses?.rounds;
  if (Array.isArray(rounds) && rounds.length > 0) return true;
  return false;
}

/**
 * Whether order tracking should start in rounds mode. Food and drinks arrive in
 * waves — courses at dinner, a second round at the bar — so rounds are the
 * default shape for anything that tracks orders. An activity that already has
 * flat orders keeps them, and existing rounds always win.
 */
export function defaultUsesRounds(
  type: EntityType | string | undefined,
  customResponses: Record<string, unknown> | null | undefined,
): boolean {
  const rounds = customResponses?.rounds;
  if (Array.isArray(rounds) && rounds.length > 0) return true;
  const orders = customResponses?.orders;
  if (Array.isArray(orders) && orders.length > 0) return false;
  return interactionUsesOrders(type, customResponses);
}

/**
 * True when an interaction should surface the trivia module: a trivia
 * quick-activity, anything with trivia already recorded, or an activity where
 * trivia was added by hand (`hasTrivia`) — a lounge running a trivia night, say.
 */
export function interactionShowsTrivia(
  customResponses: Record<string, unknown> | null | undefined,
): boolean {
  if (customResponses?.activityType === "trivia") return true;
  if (customResponses?.hasTrivia === true) return true;
  const questions = customResponses?.questions;
  if (Array.isArray(questions) && questions.length > 0) return true;
  return !!customResponses?.score || !!customResponses?.winningScore;
}
